import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { projects, Project, ENGAGEMENT_LABEL } from '../data/projects';
import { categoryTheme } from '../data/categoryTheme';
import ProjectArtBand, { ProjectCategoryPill } from './ProjectArt';
import { Eyebrow } from './bandedStyles';

/**
 * A short strip of catalogue entries, for placing on pages that make a claim the
 * catalogue can back up.
 *
 * The point is not decoration: a service page that says "we do Azure migrations"
 * is an assertion, and a link to three of them is evidence. It also gives the
 * catalogue internal links from the pages that rank, which is the only way those
 * detail pages get discovered by anything other than the index.
 *
 * Filter by `category` (service line) or `capability`, or pass neither to take
 * the most recent entries. Renders nothing at all when nothing matches — a
 * heading over an empty row is worse than no section.
 */
export interface RelatedProjectsProps {
  /** Match a service category exactly, e.g. "Cloud & Identity". */
  category?: string;
  /** Match a capability, e.g. "AI & Machine Learning". */
  capability?: string;
  /**
   * Hand-picked entries, in the given order. For essays, where the pairing is an
   * editorial judgement rather than a category match. Takes precedence over the
   * filters; unknown slugs are dropped rather than rendered as gaps.
   *
   * A page may render at most ONE curated strip alongside its filtered strip.
   * Two strips share one component instance each, so each needs a distinct
   * heading id — pass `idSuffix` on every strip after the first.
   */
  slugs?: string[];
  /** Omit the project the reader is already looking at. */
  excludeSlug?: string;
  limit?: number;
  heading?: string;
  intro?: string;
  /** Disambiguates the section heading id when a page renders two strips. */
  idSuffix?: string;
  /**
   * The banded page (round 27): left-aligned heading with an optional eyebrow, no
   * outer margin (the page spaces its own blocks), and the cards in one
   * horizontally scrolling scroll-snap row on phones, four across from 1000px.
   * The cards are links, so the row is keyboard-reachable without a tab stop of
   * its own.
   */
  scrollRow?: boolean;
  /** Small caps line above the heading (scrollRow only). */
  eyebrow?: string;
  /**
   * scrollRow only: on phones each card is a painting and a title; the one-line summary shows from
   * 700px. The summary text stays in the HTML at every width (the catalogue page carries it in full).
   */
  compactOnPhones?: boolean;
}

const Section = styled.section<{ $scrollRow?: boolean }>`
  margin-top: ${({ $scrollRow }) => ($scrollRow ? '0' : 'var(--spacing-3xl)')};
  ${({ $scrollRow }) => $scrollRow && 'position: relative; z-index: 2;'}
`;

const Heading = styled.h2<{ $scrollRow?: boolean }>`
  font-size: var(--fs-2xl);
  font-weight: ${({ $scrollRow }) => ($scrollRow ? 'var(--font-weight-extra-bold)' : 'var(--font-weight-bold)')};
  color: ${({ $scrollRow }) => ($scrollRow ? 'var(--davinci-midnight)' : 'var(--text-primary)')};
  margin-bottom: var(--spacing-sm);
  ${({ $scrollRow }) => $scrollRow && `
    margin-bottom: 0;
    font-size: var(--fs-xl);
    line-height: var(--lh-snug);
    letter-spacing: -0.01em;

    @media (min-width: 768px) {
      font-size: var(--fs-2xl);
    }
  `}
`;

const Intro = styled.p<{ $scrollRow?: boolean }>`
  color: var(--text-secondary);
  line-height: var(--lh-relaxed);
  margin-bottom: ${({ $scrollRow }) => ($scrollRow ? '18px' : 'var(--spacing-xl)')};
  ${({ $scrollRow }) => $scrollRow && 'margin-top: 0.55rem; max-width: 760px; line-height: var(--lh-normal);'}
`;

/*
 * `auto-fit` with an explicit minimum, NOT `repeat(3, 1fr)`.
 *
 * This strip is dropped into containers of very different widths — a ~608px
 * blog reading column, an ~820px project article, a full-width service page.
 * A hard three-column track overflowed the narrow one by 89px, because `1fr`
 * floors at min-content and a card will not compress below ~211px. `auto-fit`
 * lets the row settle on however many columns actually fit, and the `min()`
 * guard keeps a single card from overflowing a very narrow phone.
 */
const Row = styled.div<{ $scrollRow?: boolean }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
  gap: var(--spacing-lg);

  ${({ $scrollRow }) => $scrollRow && `
    /* One row that scrolls sideways and snaps to a card. The extra block padding
       keeps the card shadow and hover lift from being clipped by the scroller. */
    grid-template-columns: none;
    grid-auto-flow: column;
    grid-auto-columns: 82%;
    gap: 14px;
    margin: 0 -2px;
    padding: 4px 2px 12px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scrollbar-width: thin;

    > a {
      scroll-snap-align: start;
    }

    @media (min-width: 700px) {
      grid-auto-columns: 42%;
    }

    @media (min-width: 1000px) {
      grid-auto-flow: row;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      grid-auto-columns: auto;
      overflow: visible;
    }
  `}
`;

const MiniCard = styled(Link)<{ $accent: string; $scrollRow?: boolean }>`
  /* The card's inner padding, shared with the painted header that cancels it. */
  --card-pad: ${({ $scrollRow }) => ($scrollRow ? '14px' : 'var(--spacing-lg)')};
  display: flex;
  flex-direction: column;
  gap: ${({ $scrollRow }) => ($scrollRow ? '0.45rem' : 'var(--spacing-sm)')};
  padding: var(--card-pad);
  background: var(--white);
  border: 1px solid var(--border-light);
  border-top: 3px solid ${({ $accent }) => $accent};
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  transition: transform var(--transition-fast), box-shadow var(--transition-fast);

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-md);
  }

  &:focus-visible {
    outline: 3px solid var(--davinci-blue-fill);
    outline-offset: 2px;
  }
`;

/* The painted header runs edge to edge, so it cancels the card's padding. */
const MiniArt = styled(ProjectArtBand)`
  margin: calc(-1 * var(--card-pad)) calc(-1 * var(--card-pad)) 0;
`;

/* CLAUDE.md gotcha 5: in-house platforms are never blended into client work. */
const InHouseBadge = styled.span`
  align-self: flex-start;
  padding: 2px var(--spacing-sm);
  border: 1px solid var(--davinci-denim);
  border-radius: 4px;
  font-size: max(13px, var(--fs-xs));
  font-weight: var(--font-weight-semibold);
  letter-spacing: var(--ls-wide);
  text-transform: uppercase;
  color: var(--davinci-denim);
`;

const MiniTitle = styled.span<{ $scrollRow?: boolean }>`
  font-size: ${({ $scrollRow }) => ($scrollRow ? 'var(--fs-sm)' : 'var(--fs-base)')};
  font-weight: var(--font-weight-bold);
  color: ${({ $scrollRow }) => ($scrollRow ? 'var(--davinci-midnight)' : 'var(--text-primary)')};
  line-height: var(--lh-snug);
`;

const MiniSummary = styled.span<{ $scrollRow?: boolean; $compact?: boolean }>`
  font-size: ${({ $scrollRow }) => ($scrollRow ? 'max(13px, var(--fs-xs))' : 'var(--fs-sm)')};
  color: var(--text-secondary);
  line-height: ${({ $scrollRow }) => ($scrollRow ? 'var(--lh-normal)' : 'var(--lh-relaxed)')};

  ${({ $compact }) => $compact && `
    @media (max-width: 699px) {
      display: none;
    }
  `}
`;

const AllLink = styled(Link)`
  display: inline-block;
  margin-top: var(--spacing-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--davinci-blue-fill);
`;

const RelatedProjects: React.FC<RelatedProjectsProps> = ({
  category,
  capability,
  slugs,
  excludeSlug,
  limit = 3,
  heading = 'Related work',
  intro,
  idSuffix = 'related-projects-heading',
  scrollRow = false,
  eyebrow,
  compactOnPhones = false,
}) => {
  const curated = slugs
    ? slugs
        .map((slug) => projects.find((project) => project.slug === slug))
        .filter((project): project is Project => Boolean(project))
    : null;

  const matches = (curated
    ?? projects
      .filter((project) => (category ? project.serviceCategory === category : true))
      .filter((project) => (capability ? project.capabilities.includes(capability) : true)))
    .filter((project: Project) => project.slug !== excludeSlug)
    .slice(0, limit);

  if (matches.length === 0) return null;

  return (
    <Section aria-labelledby={idSuffix} $scrollRow={scrollRow}>
      {scrollRow && eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <Heading id={idSuffix} $scrollRow={scrollRow}>{heading}</Heading>
      {intro && <Intro $scrollRow={scrollRow}>{intro}</Intro>}
      <Row $scrollRow={scrollRow}>
        {matches.map((project) => {
          const theme = categoryTheme(project.serviceCategory);
          return (
            <MiniCard
              key={project.slug}
              to={`/projects/${project.slug}`}
              $accent={theme.accent}
              $scrollRow={scrollRow}
            >
              <MiniArt slug={project.slug} variant="mini">
                <ProjectCategoryPill category={project.serviceCategory} />
              </MiniArt>
              {project.engagement === 'in-house' && (
                <InHouseBadge>{ENGAGEMENT_LABEL['in-house']}</InHouseBadge>
              )}
              <MiniTitle $scrollRow={scrollRow}>{project.title}</MiniTitle>
              <MiniSummary $scrollRow={scrollRow} $compact={scrollRow && compactOnPhones}>{project.cardSummary}</MiniSummary>
            </MiniCard>
          );
        })}
      </Row>
      <AllLink to="/projects">See the full project catalogue →</AllLink>
    </Section>
  );
};

export default RelatedProjects;
